/**
 * Hesap durumu — kiracı tarafı (v3.1 Dalga 1 B3; ADR-0015 § 5).
 *  - Deneme uyarısı: bitişe 7, 3 ve 1 gün kala (SÜRE esaslı; eşik sunucuda hesaplanır). Bitiş anı çalışma alanının saat
 *    diliminde gösterilir.
 *  - Deneme sonu: yeni kayıt ve değişiklik kapalıdır; okuma, arama ve dışa aktarma açıktır. Şerit her sayfada görünür.
 *  - Askı: herhangi bir istek 423 `TENANT_SUSPENDED` dönerse kabuk askı ekranına geçer (bilgi + çıkış; başka işlem yok).
 * Veri hiçbir durumda silinmez; metinler sunucudan da gelir (`message`) ama arayüz kendi Türkçe metnini kullanır.
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Clock, Lock } from 'lucide-react';
import { accountStatusSchema, TENANT_LIFECYCLE_TEXT, type AccountStatus } from '@castinflow/shared';
import { Alert, Button, Wordmark } from '@castinflow/ui';
import { apiFetch, onTenantSuspended } from '../lib/api';
import { qk, useScope } from '../lib/scope';
// Kabuk ana pakettedir: çerçeve modülleri (metinleriyle) tembel yüklenir; tarih yalnız hafif `lib/dates`ten (K-M7-50.5)
import { formatDateTimeTr } from '../lib/dates';
import { tr } from '../i18n/tr';

/** Hesap durumu sorgusu (oturum açık kabukta; yan etkisiz GET) */
export function useAccountStatus() {
  const scope = useScope();
  return useQuery({
    queryKey: scope ? qk.accountStatus(scope) : ['account-status', 'none'],
    queryFn: async () => accountStatusSchema.parse(await apiFetch('/account/status')),
    enabled: scope !== null,
    staleTime: 300_000,
    retry: false,
  });
}

/** Çalışma alanı askıdaysa gösterilecek ileti (herhangi bir isteğin 423'ü ya da hesap durumu); değilse `null` */
export function useTenantSuspended(): string | null {
  const [message, setMessage] = useState<string | null>(null);
  const status = useAccountStatus();
  useEffect(
    () =>
      onTenantSuspended(() => {
        setMessage(TENANT_LIFECYCLE_TEXT.suspended);
      }),
    [],
  );
  if (message !== null) return message;
  return status.data?.status === 'suspended' ? TENANT_LIFECYCLE_TEXT.suspended : null;
}

const endsText = (s: AccountStatus): string =>
  s.trial.endsAt === null ? '' : formatDateTimeTr(s.trial.endsAt, { timeZone: s.timezone });

/** Deneme uyarısı ve deneme sonu şeridi (kabukta her sayfanın üstünde) */
export function AccountStatusBanner() {
  const q = useAccountStatus();
  const s = q.data;
  if (s?.status !== 'active') return null;
  if (s.trial.writeRestricted)
    return (
      <div data-testid="trial-expired-banner">
        <Alert tone="warning" title={TENANT_LIFECYCLE_TEXT.trialExpiredTitle}>
          <p>{TENANT_LIFECYCLE_TEXT.trialExpired}</p>
          {s.trial.endsAt !== null ? (
            <p className="text-fg-muted">{tr.account.trialEnded(endsText(s))}</p>
          ) : null}
        </Alert>
      </div>
    );
  if (s.trial.warningDays !== null)
    return (
      <div
        role="region"
        aria-label={tr.account.trialWarningTitle}
        className="flex items-start gap-3 rounded-card border border-info/30 bg-info-subtle px-4 py-3 text-sm text-fg"
        data-testid="trial-warning-banner"
      >
        <Clock size={18} aria-hidden="true" className="mt-0.5 shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="font-semibold">{tr.account.trialWarningTitle}</p>
          <p className="text-fg-muted">
            {tr.account.trialWarning(s.trial.warningDays, endsText(s))}
          </p>
        </div>
      </div>
    );
  return null;
}

/** Askı ekranı: kabuğun yerine geçer; yalnız bilgi ve çıkış */
export function SuspendedScreen({ message, onLogout }: { message: string; onLogout: () => void }) {
  return (
    <div className="flex min-h-screen flex-col bg-canvas px-4 py-8 sm:py-14">
      <header className="mx-auto mb-6 w-full max-w-md">
        <Wordmark />
      </header>
      <main
        id="main-content"
        tabIndex={-1}
        className="mx-auto w-full max-w-md focus:outline-none"
        data-testid="tenant-suspended"
      >
        <div className="space-y-4 rounded-lg border border-border bg-surface p-5 sm:p-6">
          <div className="flex items-center gap-2">
            <Lock size={20} aria-hidden="true" className="text-danger" />
            <h1 className="text-lg font-semibold text-fg" data-page-title="" tabIndex={-1}>
              {TENANT_LIFECYCLE_TEXT.suspendedTitle}
            </h1>
          </div>
          <p role="status" className="text-base text-fg">
            {message}
          </p>
          <p className="text-base text-fg-muted">{tr.account.suspendedLead}</p>
          <Button onClick={onLogout} data-testid="tenant-suspended-logout">
            {tr.account.logout}
          </Button>
        </div>
      </main>
    </div>
  );
}
